import { formatDate } from '@evaneos/b2b-ui/legacy';
import { addDays } from 'date-fns';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Dropdown } from '../../../../../../design-system/Dropdown/Dropdown';
import { DayPickerFormInput } from '../../../../../../design-system/Form/DayPickerFormInput';
import { MoreVert } from '../../../../../../design-system/Icons/MoreVert';
import { InstalmentViewModel } from '../../../../../../resources/contract/types';
import { NumberFormInput } from '../NumberFormInput';
import { TextFormInput } from '../TextFormInput';

import { DeleteInstalmentModal } from './DeleteInstalmentModal';

import s from './PaymentScheduleStep.css';

type Props = {
    index: string;
    instalment: InstalmentViewModel;
    deleteInstalment: () => InstalmentViewModel | undefined;
    changeDueDate: (date: Date | undefined) => void;
    duplicateInstalment: () => void;
    currency?: string;
};

export function Instalment({
    deleteInstalment,
    index,
    instalment,
    changeDueDate,
    duplicateInstalment,
}: Props): JSX.Element {
    const intl = useIntl();

    const [isModalOpen, setModalOpen] = useState<boolean>(false);
    const [isDropdownOpened, setIsDropdownOpened] = useState(false);

    return (
        <div key={index} className={s.instalments}>
            <TextFormInput
                name={`${index}.payerName`}
                label={intl.formatMessage({
                    id: 'contract.payment_schedule.instalment.payer_name',
                })}
            />
            <TextFormInput
                name={`${index}.label`}
                label={intl.formatMessage({
                    id: 'contract.payment_schedule.instalment.label',
                })}
            />
            <div>
                <DayPickerFormInput
                    name={`${index}.dueDate`}
                    label={intl.formatMessage({
                        id: 'contract.payment_schedule.instalment.due_date',
                    })}
                    onDayChange={changeDueDate}
                    fromDate={addDays(new Date(), 1)}
                    format="dd/MM/yyyy"
                    inputProps={{
                        value: instalment.dueDate
                            ? formatDate(instalment.dueDate, 'dd/MM/yyyy')
                            : '',
                        // @ts-ignore
                        'data-testid': `${index}.dueDate`,
                    }}
                />
            </div>
            <NumberFormInput
                name={`${index}.price.amount`}
                label={'contract.payment_schedule.instalment.price'}
                containerClassName={s.amountContainer}
                labelClassName={s.hideLabel}
                currency={instalment.price.currency}
            />
            <Dropdown
                className={s.dropdownContainer}
                buttonClass={s.dropdownButton}
                dropdownClassName={s.dropdown}
                menuItemClassName={s.dropdownMenu}
                isOpened={isDropdownOpened}
                handleMenu={() => setIsDropdownOpened(!isDropdownOpened)}
                button={() => (
                    <MoreVert color="var(--ink-light)" data-testid={`${index}.optionsMenu`} />
                )}
            >
                <p
                    className={s.dropdownItem}
                    data-testid={`${index}.duplicate`}
                    onClick={() => duplicateInstalment()}
                >
                    <FormattedMessage id="contract.payment_schedule.instalment.duplicate" />
                </p>
                <p
                    className={s.dropdownItem}
                    data-testid={`${index}.delete`}
                    onClick={() => setModalOpen(true)}
                >
                    <FormattedMessage id="contract.payment_schedule.instalment.delete.button" />
                </p>
            </Dropdown>
            <DeleteInstalmentModal
                isOpen={isModalOpen}
                close={() => setModalOpen(false)}
                submit={() => {
                    deleteInstalment();
                    setModalOpen(false);
                }}
            />
        </div>
    );
}
